
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAccessCodeDebugger } from "@/hooks/useAccessCodeDebugger";
import { Clock, TestTube, Settings, Rocket, CheckCircle, AlertCircle, Play, Activity } from "lucide-react";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { getSupabaseConfig } from "@/utils/environmentUtils";

export const AccessCodeDebugPanel = () => {
  const {
    testAccessCodeGeneration,
    testNewEdgeFunctionGeneration,
    isTestingGeneration,
    isTestingNewEdgeFunction,
  } = useAccessCodeDebugger();

  const [isTestingCronCall, setIsTestingCronCall] = useState(false);
  const [isCheckingLogs, setIsCheckingLogs] = useState(false);
  const { toast } = useToast();

  const testDirectCronCall = async () => {
    setIsTestingCronCall(true);
    try {
      console.log('Testing direct cron call to edge function...');
      
      // Get the Supabase configuration
      const config = getSupabaseConfig();
      
      // Simulate the exact call that the cron job makes
      const response = await fetch(
        'https://doyqmjwlizoqfuabvluf.supabase.co/functions/v1/auto-generate-access-codes',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${config.anonKey}`,
          },
          body: JSON.stringify({
            source: 'manual_test',
            timestamp: new Date().toISOString(),
          }),
        }
      );

      const data = await response.json();
      
      console.log('Direct cron call response:', data);
      
      if (response.ok && data.success) {
        toast({
          title: "Direct Cron Call Successful",
          description: `Processed ${data.bookings_processed} bookings, generated ${data.successfully_generated} codes`,
          variant: "default",
        });
      } else {
        toast({
          title: "Direct Cron Call Failed",
          description: data.error || `HTTP ${response.status}: ${response.statusText}`,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Direct cron call failed:', error);
      toast({
        title: "Direct Cron Call Error",
        description: error.message || "Network or connection error",
        variant: "destructive",
      });
    } finally {
      setIsTestingCronCall(false);
    }
  };

  const checkSystemLogs = async () => {
    setIsCheckingLogs(true);
    try {
      console.log('Checking recent system logs...');
      
      const { data, error } = await supabase
        .from('error_logs')
        .select('*')
        .in('error_type', ['system', 'access_code_generation'])
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) {
        throw error;
      }

      console.log('Recent system logs:', data);
      
      const recentAccessCodeLogs = data?.filter(log => 
        log.error_message?.includes('access code') || 
        log.error_type === 'access_code_generation'
      ) || [];

      toast({
        title: "System Logs Retrieved",
        description: `Found ${data?.length || 0} recent logs, ${recentAccessCodeLogs.length} access code related`,
        variant: "default",
      });

    } catch (error) {
      console.error('Failed to check logs:', error);
      toast({
        title: "Log Check Failed",
        description: error.message || "Failed to retrieve system logs",
        variant: "destructive",
      });
    } finally {
      setIsCheckingLogs(false);
    }
  };

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Access Code Generation Debug Panel
        </CardTitle>
        <CardDescription>
          Test and debug the automated access code generation system
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-2">
            <h3 className="text-sm font-medium flex items-center gap-2">
              <TestTube className="h-4 w-4" />
              Debug Test (Legacy)
            </h3>
            <p className="text-sm text-muted-foreground">
              Run a comprehensive test of the legacy debug system
            </p>
            <Button
              onClick={() => testAccessCodeGeneration.mutate()}
              disabled={isTestingGeneration}
              className="w-full"
              variant="outline"
            >
              {isTestingGeneration ? (
                <>
                  <Clock className="mr-2 h-4 w-4 animate-spin" />
                  Running Debug Test...
                </>
              ) : (
                <>
                  <TestTube className="mr-2 h-4 w-4" />
                  Run Debug Test
                </>
              )}
            </Button>
          </div>

          <div className="space-y-2">
            <h3 className="text-sm font-medium flex items-center gap-2">
              <Rocket className="h-4 w-4" />
              Current Edge Function
            </h3>
            <p className="text-sm text-muted-foreground">
              Test the current edge function-based access code generation
            </p>
            <Button
              onClick={() => testNewEdgeFunctionGeneration.mutate()}
              disabled={isTestingNewEdgeFunction}
              className="w-full"
              variant="default"
            >
              {isTestingNewEdgeFunction ? (
                <>
                  <Clock className="mr-2 h-4 w-4 animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <Rocket className="mr-2 h-4 w-4" />
                  Test Current System
                </>
              )}
            </Button>
          </div>

          <div className="space-y-2">
            <h3 className="text-sm font-medium flex items-center gap-2">
              <Play className="h-4 w-4" />
              Direct Cron Call
            </h3>
            <p className="text-sm text-muted-foreground">
              Simulate the exact cron job call to the edge function
            </p>
            <Button
              onClick={testDirectCronCall}
              disabled={isTestingCronCall}
              className="w-full"
              variant="secondary"
            >
              {isTestingCronCall ? (
                <>
                  <Clock className="mr-2 h-4 w-4 animate-spin" />
                  Testing Cron...
                </>
              ) : (
                <>
                  <Play className="mr-2 h-4 w-4" />
                  Test Cron Call
                </>
              )}
            </Button>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <h3 className="text-sm font-medium flex items-center gap-2">
              <Activity className="h-4 w-4" />
              System Logs
            </h3>
            <p className="text-sm text-muted-foreground">
              Check recent system and access code generation logs
            </p>
            <Button
              onClick={checkSystemLogs}
              disabled={isCheckingLogs}
              className="w-full"
              variant="outline"
            >
              {isCheckingLogs ? (
                <>
                  <Clock className="mr-2 h-4 w-4 animate-spin" />
                  Checking Logs...
                </>
              ) : (
                <>
                  <Activity className="mr-2 h-4 w-4" />
                  Check System Logs
                </>
              )}
            </Button>
          </div>

          <div className="space-y-2">
            <h3 className="text-sm font-medium">Quick Status Check</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Edge Function Config:</span>
                <Badge variant="default" className="bg-green-100 text-green-800">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Added to config.toml
                </Badge>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Cron Job:</span>
                <Badge variant="default" className="bg-blue-100 text-blue-800">
                  <Activity className="h-3 w-3 mr-1" />
                  Running every minute
                </Badge>
              </div>
            </div>
          </div>
        </div>

        <div className="pt-4 border-t">
          <div className="flex items-center justify-between text-sm mb-4">
            <span className="text-muted-foreground">Access Code System Status:</span>
            <Badge variant="default" className="bg-green-100 text-green-800">
              <CheckCircle className="h-3 w-3 mr-1" />
              Edge Function Active
            </Badge>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Check-in Webhook System Status:</span>
            <Badge variant="default" className="bg-green-100 text-green-800">
              <CheckCircle className="h-3 w-3 mr-1" />
              Cron Job Active
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Both systems are now running on their respective cron schedules. Access codes are generated via the edge function, and check-in webhooks are sent 5 minutes before booking start times.
          </p>
        </div>

        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <h4 className="text-sm font-medium text-green-800 mb-2 flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            System Status: Configuration Updated
          </h4>
          <ul className="text-xs text-green-700 space-y-1">
            <li>• ✅ Access code generation: Edge function added to config.toml</li>
            <li>• ✅ Edge function deployment: Will be deployed automatically</li>
            <li>• ✅ Cron job: Running every minute, calling edge function</li>
            <li>• ✅ Check-in webhooks: Separate cron job running every minute</li>
            <li>• ✅ Enhanced debugging: Multiple test options available</li>
          </ul>
        </div>

        <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
          <h4 className="text-sm font-medium text-amber-800 mb-2 flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            Next Steps
          </h4>
          <ul className="text-xs text-amber-700 space-y-1">
            <li>• Test the "Direct Cron Call" to verify edge function deployment</li>
            <li>• Monitor system logs for any errors or successful executions</li>
            <li>• Check edge function logs in Supabase dashboard</li>
            <li>• Verify access codes are being generated for upcoming bookings</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};
